import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {ActionMethod} from 'constants/AnalyticsConstants';
import * as EntryDayOps from 'notebook/entries/EntryDayOps';
import ExternalFile from 'external-files/ExternalFile';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import ProtocolStoreTransforms from 'notebook/protocols/ProtocolStoreTransforms';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import pick from 'util/pick';
import {StructuredTableMappingsStateManager} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {trackUpdateStructuredTableMappingsEvent} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsAnalytics';
import prepareStructuredTableMappingsForStore from 'notebook/structured-tables/structured-table-mappings/prepareStructuredTableMappingsForStore';
import prepareStructuredTableMappingsForSave from 'notebook/structured-tables/structured-table-mappings/prepareStructuredTableMappingsForSave';

const EntryVersionUtil = {};

EntryVersionUtil.pickRevertableFields = (entryVersion) =>
  pick(entryVersion, 'note', 'entryProtocols', 'structuredTableMappings');

EntryVersionUtil.sanitizeEntryVersion = function (entryVersion, oldEntryVersion, documentId) {
  const sanitizedEntryVersionMergeOp = {
    note: NoteStoreTransforms.sanitizeNote(entryVersion.note, oldEntryVersion?.note),
    entryProtocols: Array.from(entryVersion.entryProtocols).map((p) => EntryVersionUtil.sanitizeEP(p)),
    structuredTableMappings: sanitizeStructuredTableMappings(entryVersion),
  };

  // reference equality is acceptable here because we keep the same table mappings array if it's unchanged
  if (sanitizedEntryVersionMergeOp.structuredTableMappings !== entryVersion.structuredTableMappings) {
    trackUpdateStructuredTableMappingsEvent(
      ActionMethod.SANITIZE_NOTE,
      documentId,
      undefined,
      sanitizedEntryVersionMergeOp.structuredTableMappings,
      getAllStructuredTablesInNote(sanitizedEntryVersionMergeOp.note),
      null
    );
  }

  return reactUpdate(entryVersion, {
    $merge: sanitizedEntryVersionMergeOp,
  });
};

// Returns a copy of entryVersion.structuredTableMappings without invalid mappings, such as those that
// reference a deleted table or column.
function sanitizeStructuredTableMappings(entryVersion) {
  const mappings = entryVersion.structuredTableMappings;
  if (!mappings?.length) {
    return mappings;
  }

  return StructuredTableMappingsStateManager.removeInvalidColumnMappings(
    StructuredTableMappingsStateManager.setStructuredTableMappings(mappings),
    getAllStructuredTablesInNote(entryVersion.note)
  ).structuredTableMappings;
}

EntryVersionUtil.prepareExternalProtocolForStore = function (externalProtocol, cidIdMap) {
  externalProtocol.cid = _.uniqueId('ef');
  cidIdMap[externalProtocol.cid] = externalProtocol.id;
  delete externalProtocol.id;
  return externalProtocol;
};

EntryVersionUtil.sanitizeEP = function (entryProtocol) {
  if (entryProtocol.externalFile != null) {
    return entryProtocol;
  }
  return ProtocolStoreTransforms.sanitizeProtocol(entryProtocol);
};

/**
 * Prepares an EntryVersion that is from the server to be loaded into the client side store.  This includes populating the `cidIdMap` and flattening
 * out the Entry Days and NoteParts.
 *
 * WARNING: this function will mutate the `entryVersion` by deleting the `selectors` attribute, which will cause test flakes unless you have
 * cloned the `entryVersion`.  It will also mutate the `cidIdMap`.
 *
 * @returns an EntryVersion that is ready for the client-side Store
 */
EntryVersionUtil.prepareForStore = function (entryVersion, cidIdMap, fileId) {
  // Normalize into entryDays and noteParts
  const days = [];
  const noteParts = [];
  for (const entryDay of Array.from(entryVersion.entryDays)) {
    const day = EntryDayOps.makeDay(
      entryDay.date,
      entryDay.title,
      noteParts.length,
      entryDay.provenanceUuid,
      entryDay.sectionColor
    );
    cidIdMap[day.cid] = entryDay.id;
    cidIdMap[day.noteVersionCid] = entryDay.note.id;
    noteParts.push(...entryDay.note.noteParts);
    days.push(day);
  }
  const base = pick(entryVersion, 'id', 'parentId', 'created_at', 'modified_at', 'creator', 'entryProtocols');

  // Construct flattened note model from entryDays/noteParts with selectors pulled from entryVersion
  const note = {
    creator: entryVersion.creator,
    selectors: entryVersion.selectors,
    noteRuntimeSnapshot: entryVersion.noteRuntimeSnapshot,
    isProtected: entryVersion.isProtected,
    noteParts,
    days,
  };
  // TODO (BNCH-120800): investigate why selectors object property are being deleted
  delete entryVersion.selectors;
  base.note = note;

  // Register cid/id in cidIdMap
  base.cid = _.uniqueId('ev');
  cidIdMap[base.cid] = base.id;
  delete base.id;

  // move table mappings into a separate, more manageable state
  const structuredTableMappings = prepareStructuredTableMappingsForStore(
    fileId,
    cidIdMap,
    entryVersion.structuredTableMappings ?? []
  );
  base.structuredTableMappings = structuredTableMappings;

  return reactUpdate(base, {
    $merge: {
      note: NoteStoreTransforms.prepareForStore(base.note, cidIdMap),
      entryProtocols: Array.from(base.entryProtocols).map((p) =>
        EntryVersionUtil.prepareEPForStore(p, cidIdMap)
      ),
    },
  });
};

EntryVersionUtil.prepareForSave = function (entryVersion, cidIdMap, fileId) {
  const base = pick(entryVersion, 'cid', 'entryProtocols', 'note');
  base.id = cidIdMap[base.cid]; // Restore id from cid

  let preparedEV = reactUpdate(base, {
    $merge: {
      note: NoteStoreTransforms.prepareForSave(base.note, cidIdMap, fileId),
      entryProtocols: Array.from(base.entryProtocols).map((p) =>
        EntryVersionUtil.prepareEPForSave(p, cidIdMap)
      ),
    },
  });

  // De-normalize entryDays and noteParts
  let entryDays = [];
  const {noteParts} = preparedEV.note;
  entryDays = base.note.days.map((day, i) => {
    const nextDay = base.note.days[i + 1];
    const note = {
      id: cidIdMap[day.noteVersionCid],
      cid: day.noteVersionCid,
      noteParts: nextDay != null ? noteParts.slice(day.index, nextDay.index) : noteParts.slice(day.index),
    };
    return {
      date: day.date,
      title: day.title,
      id: cidIdMap[day.cid],
      cid: day.cid,
      provenanceUuid: day.provenanceUuid,
      sectionColor: day.sectionColor,
      note,
    };
  });

  // Replace flattened note model with entryDays and selectors
  preparedEV.entryDays = entryDays;
  preparedEV.selectors = preparedEV.note.selectors;
  preparedEV.noteRuntimeSnapshot = preparedEV.note.noteRuntimeSnapshot;
  preparedEV.isProtected = base.note.isProtected ?? false;
  delete preparedEV.note;

  // bring in the table mappings from its separate state
  const structuredTableMappings = prepareStructuredTableMappingsForSave(fileId, cidIdMap);
  preparedEV = {
    ...preparedEV,
    structuredTableMappings,
  };
  return preparedEV;
};

EntryVersionUtil.prepareEPForStore = function (entryProtocol, cidIdMap) {
  const {id, externalFile, originalProtocolId} = entryProtocol;

  // TODO(T1044): protocol should be keyed under protocolVersion
  // EntryProtocol dict and Protocol dict are combined, so the protocol actually
  // has two ids that correspond to it. We add a second cid so that we can properly
  // keep track of the protocol version and the entry protocol version.

  // Parse protocol and rename new fields
  if (entryProtocol.externalFile) {
    entryProtocol = EntryVersionUtil.prepareExternalProtocolForStore(entryProtocol, cidIdMap);
  } else {
    entryProtocol = ProtocolStoreTransforms.prepareForStore(entryProtocol, cidIdMap);
  }

  entryProtocol.pvCid = entryProtocol.cid;
  entryProtocol.cid = _.uniqueId('ep');
  cidIdMap[entryProtocol.cid] = id;
  delete entryProtocol.id;

  // Ensure entryProtocol fields are set.
  entryProtocol.externalFile = externalFile;
  entryProtocol.originalProtocolId = originalProtocolId;

  return entryProtocol;
};

EntryVersionUtil.prepareEPForSave = function (entryProtocol, cidIdMap) {
  const {cid, name} = entryProtocol;
  const d = {
    cid,
    name,
    id: cidIdMap[entryProtocol.cid],
  };

  // Handle external file vs. protocol
  const {externalFile, originalProtocolId, pvCid} = entryProtocol;
  if (externalFile != null) {
    d.externalFile = externalFile;
    return d;
  }

  // Otherwise, rename fields for protocol preparation.
  const protocolVersion = reactUpdate(entryProtocol, {$merge: {cid: pvCid}});
  const preparedProtocolVersion = ProtocolStoreTransforms.preparePVForSave(protocolVersion, cidIdMap);

  // and add protocol fields
  d.originalProtocolId = originalProtocolId;
  d.protocolVersion = preparedProtocolVersion;
  return d;
};

EntryVersionUtil.withEntryProtocolUpdated = (entryVersion, protocolCid, op) =>
  reactUpdate(entryVersion, {
    entryProtocols: {
      $set: entryVersion.entryProtocols.map((entryProtocol) => {
        if (entryProtocol.cid === protocolCid) {
          entryProtocol = reactUpdate(entryProtocol, op);
        }
        return entryProtocol;
      }),
    },
  });

EntryVersionUtil.withProtocolAdded = function (entryVersion, protocol, cidIdMap) {
  let entryProtocol;
  if (protocol.externalFile) {
    const {externalFile} = protocol;
    entryProtocol = {
      externalFile,
      name: ExternalFile.toName(externalFile),
      originalProtocolId: null,
      inEntry: true,
      id: null,
    };
  } else {
    // eslint-disable-next-line no-restricted-syntax
    entryProtocol = _.extend({}, protocol, {
      originalProtocolId: protocol.id,
      inEntry: true,
      id: null,
    });
  }
  const preparedEntryProtocol = EntryVersionUtil.prepareEPForStore(entryProtocol, cidIdMap);
  return reactUpdate(entryVersion, {
    entryProtocols: {$set: [...entryVersion.entryProtocols, preparedEntryProtocol]},
  });
};

EntryVersionUtil.withEntryProtocolRemoved = (entryVersion, epCid) =>
  reactUpdate(entryVersion, {
    entryProtocols: {
      $set: entryVersion.entryProtocols.filter((entryProtocol) => entryProtocol.cid !== epCid),
    },
  });

EntryVersionUtil.withEntryProtocolReverted = (entryVersion, revertedEntryProtocol) =>
  reactUpdate(entryVersion, {
    entryProtocols: {
      $set: entryVersion.entryProtocols.map((entryProtocol) => {
        if (entryProtocol.cid === revertedEntryProtocol.cid) {
          return revertedEntryProtocol;
        } else {
          return entryProtocol;
        }
      }),
    },
  });

EntryVersionUtil.withEntryProtocolsReordered = (entryVersion, cids) =>
  reactUpdate(entryVersion, {
    entryProtocols: {
      $set: cids.map((cid) => _.findWhere(entryVersion.entryProtocols, {cid})),
    },
  });

export default EntryVersionUtil;
